/** Lightweight context menu for row/column operations (F-13). */
import { createPortal } from "react-dom";
import { useKolaPortalTarget } from "../../ui/KolaRoot.js";

export interface MenuItem {
  label: string;
  danger?: boolean;
  separatorBefore?: boolean;
  /**
   * Render the item greyed and inert (F-28: the locked "Original Script"
   * column's delete). Deliberately shown-but-disabled rather than hidden —
   * the row's own "Delete row" sits in the same menu, so an item silently
   * missing would read as a bug; greyed + a `title` says WHY.
   */
  disabled?: boolean;
  /** Hover/disabled explanation. */
  title?: string;
  onClick: () => void;
}

export interface MenuState {
  x: number;
  y: number;
  items: MenuItem[];
}

export interface ContextMenuProps {
  menu: MenuState;
  onClose: () => void;
}

export function ContextMenu({ menu, onClose }: ContextMenuProps) {
  // The body-level layer that carries Kola's tokens (ui/KolaRoot.tsx) —
  // plain <body> has none once app.css is scoped under .kola-root.
  const portalTarget = useKolaPortalTarget();
  // Keep the menu on-screen for clicks near the right/bottom edges.
  const x = Math.min(menu.x, window.innerWidth - 200);
  const y = Math.min(menu.y, window.innerHeight - menu.items.length * 32 - 16);

  // Portal to <body>: the menu opens from inside the transformed
  // .camera-layer (F-121 pan/zoom uses translate3d), and a transformed
  // ancestor becomes the containing block for position:fixed — rendered in
  // place, the menu would land offset by the camera translation (arbitrarily
  // far after panning) and the inset:0 backdrop would collapse to the camera
  // layer's zero-sized box, breaking click-away dismissal. In <body> the
  // fixed coordinates resolve against the real viewport.
  return createPortal(
    <>
      <div
        className="context-menu-backdrop"
        // pointerdown, NOT mousedown: a long-press opens the menu while the
        // finger is still down, and lifting it fires compatibility MOUSE
        // events — a mousedown backdrop would close the menu on that lift.
        // A fresh press (pointerdown) is the only thing that dismisses.
        onPointerDown={onClose}
        onContextMenu={(event) => {
          event.preventDefault();
          onClose();
        }}
      />
      <div className="context-menu" style={{ left: x, top: y }} role="menu">
        {menu.items.map((item, i) => (
          <div key={i}>
            {item.separatorBefore === true && <div className="separator" />}
            <button
              type="button"
              className={item.danger === true ? "danger" : ""}
              disabled={item.disabled === true}
              {...(item.title !== undefined ? { title: item.title } : {})}
              onClick={() => {
                onClose();
                item.onClick();
              }}
            >
              {item.label}
            </button>
          </div>
        ))}
      </div>
    </>,
    portalTarget,
  );
}
